import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

export class MediaGallery extends Component {
  #controller;
  #selectionVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#selectionVersion++;
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const version = ++this.#selectionVersion;
    event.promise.then(async ({ detail }) => {
      if (!detail?.html || !this.isConnected || version !== this.#selectionVersion) return;
      const next = detail.html.querySelector('media-gallery');
      if (!next) return;
      const signature = gallery => [...gallery.querySelectorAll('.product-media__image')].map(image => image.getAttribute('src')).join('|');
      if (signature(this) === signature(next)) return;
      const images = [...next.querySelectorAll('.product-media__image')];
      await Promise.all(images.map(async image => {
        image.loading = 'eager';
        const preload = new Image();
        preload.sizes = image.sizes;
        preload.srcset = image.srcset;
        preload.src = image.src;
        await preload.decode();
      }));
      if (!this.isConnected || version !== this.#selectionVersion) return;
      const visible = [...this.querySelectorAll('.product-media__image')].find(image => {
        const rect = image.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < innerWidth && rect.bottom > 0 && rect.top < innerHeight && !image.closest('zoom-dialog');
      });
      const host = this.parentElement;
      let cover;
      if (visible && host && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const rect = visible.getBoundingClientRect();
        const hostRect = host.getBoundingClientRect();
        const style = getComputedStyle(visible);
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        cover = visible.cloneNode(false);
        for (const name of ['id', 'ref', 'srcset', 'sizes']) cover.removeAttribute(name);
        cover.src = visible.currentSrc || visible.src;
        cover.className = 'gb-variant-cover';
        cover.setAttribute('aria-hidden', 'true');
        cover.alt = '';
        Object.assign(cover.style, { position: 'absolute', left: (rect.left - hostRect.left + host.scrollLeft) + 'px', top: (rect.top - hostRect.top + host.scrollTop) + 'px', width: rect.width + 'px', height: rect.height + 'px', objectFit: style.objectFit, objectPosition: style.objectPosition, zIndex: '5', pointerEvents: 'none', margin: '0', opacity: '1', transition: 'none', transform: 'none', backgroundColor: style.backgroundColor });
        await cover.decode();
        if (!this.isConnected || version !== this.#selectionVersion) return;
        host.querySelectorAll(':scope > .gb-variant-cover').forEach(node => node.remove());
        host.append(cover);
      }
      this.replaceWith(next);
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      next.slideshow?.select(0, undefined, { animate: false });
      if (cover) {
        const remove = () => cover.remove();
        const clearOnInteraction = e => { if (e.target.closest?.('slideshow-arrow, slideshow-controls, .slideshow-control')) remove(); };
        next.addEventListener('click', clearOnInteraction);
        try {
          await cover.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 1800, easing: 'ease-in-out', fill: 'forwards' }).finished;
        } finally {
          remove();
          next.removeEventListener('click', clearOnInteraction);
        }
      }
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[media-gallery] Image update failed:', error);
    });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const media = this.refs.zoomDialogComponent?.refs.media[index];
    if (media) this.refs.zoomDialogComponent?.loadHighResolutionImage(media);
  }

  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
